"use client";

import { Delete } from "@ellaandil/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddVoucher, useRemoveVoucher } from "@trydig/centra/client";
import { useEffect } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import z from "zod";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/form/Input";
import { useTranslations } from "@/i18n/client";
import { useCart } from "./hooks";

export const DiscountCodeForm = () => {
	const t = useTranslations("cart");
	const { mutate: addVoucher, isPending } = useAddVoucher();

	const discountFormSchema = z.object({
		voucher: z.string().min(1, { message: t("fieldError.required") }),
	});

	type DiscountInput = z.infer<typeof discountFormSchema>;

	const methods = useForm<DiscountInput>({
		resolver: zodResolver(discountFormSchema),
		mode: "onSubmit",
		reValidateMode: "onSubmit",
	});

	const {
		register,
		handleSubmit,
		setError,
		clearErrors,
		reset,
		watch,
		formState: { errors },
	} = methods;

	const onSubmit: SubmitHandler<DiscountInput> = ({ voucher }) => {
		addVoucher(
			{ voucher: voucher.toString() },
			{
				onError: () => {
					setError("voucher", {
						message: t("voucher.error"),
					});
				},
				onSuccess: () => {
					reset();
				},
			},
		);
	};

	const voucher = watch("voucher");

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		clearErrors("voucher");
	}, [voucher, clearErrors]);

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Input
					{...register("voucher")}
					size={"large"}
					placeholder={t("discountCode")}
					errorMessage={errors?.voucher?.message}
					isInvalid={!!errors?.voucher?.message}
					action={
						<Button type="submit" disabled={!voucher} loading={isPending}>
							{t("add")}
						</Button>
					}
				/>
			</form>
		</FormProvider>
	);
};

export const TotalPriceOff = ({ discount }: { discount: number }) => {
	const { data } = useCart();
	const mutation = useRemoveVoucher();

	return (
		<div className="flex items-center gap-1">
			<span className="text-status-success-text">
				{discount} {data?.currency}
			</span>
			<Delete
				width={14}
				height={14}
				className="bg-white p-0.5 rounded-component-specific-button cursor-pointer"
				onClick={() => {
					for (const voucher of data?.vouchers || []) {
						const code = voucher.voucher;

						if (code) {
							mutation.mutate({ voucher: code });
						}
					}
				}}
			/>
		</div>
	);
};
